import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormControl, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subject, finalize, takeUntil } from "rxjs";
import { ICredentials, INoAuthUserTelecom, ITokens } from "src/app/epicare-public/login/domain/interfaces/login.interface";
import { IOrganizationAccess } from "src/app/shared/interfaces/access.interface";
import { AuthService } from "src/app/shared/services/auth.service";
import { ConnectionEventsService } from "src/app/shared/services/connection-events.service";
import { PREVENT_CHARACTER, PreventCharacterHelper } from "src/app/shared/utils/helpers/prevent-character-helper";

@Component({
  selector: "app-login-form",
  templateUrl: "./login-form.component.html",
  styleUrls: ["./login-form.component.scss"],
})
export class LoginFormComponent implements OnInit, OnDestroy {
  @Input() showBorder = false;

  /**
   * When false, a successful login does not persist the returned tokens as the real
   * session (no access/refresh token in storage, no notifyLoginStateChanged()). The
   * tokens are still emitted through loginTokens so the caller can use them itself -
   * this is used by the invitation flow, where logging in should not make the app
   * think the user is really connected until the invitation is actually accepted.
   */
  @Input() persistSession = true;

  /**
   * When true, clicking "reset password" emits forgotPasswordClick instead of navigating to
   * the reset password page, so a caller displayed in a dialog (e.g. the session-expired login
   * dialog) can open the reset password flow in a dialog on top of itself rather than routing
   * to a page hidden underneath it.
   */
  @Input() resetPasswordInDialog = false;

  @Output() loginFormSubmit: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() loginTokens: EventEmitter<ITokens> = new EventEmitter<ITokens>();
  @Output() forgotPasswordClick: EventEmitter<void> = new EventEmitter<void>();
  show2FA = false;
  isLoginFailed = false;
  is2FAFailed = false;
  telecoms: INoAuthUserTelecom[] = [];

  public loginForm = this.fb.nonNullable.group({
    login: ["", [Validators.required]],
    password: ["", [Validators.required]],
  });

  public digits2FAForm = this.fb.nonNullable.group({
    digits: this.fb.array([]),
  });
  public nbDigit = 6;
  public PREVENT_CHARACTER = PREVENT_CHARACTER;
  public showTelecoms = false;
  public telecomToSend: INoAuthUserTelecom | undefined;
  public isUserVerified = true;
  public showPassword = false;

  private onDestroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private connectionEventsService: ConnectionEventsService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService
  ) {}

  /**
   * Cleanup subscriptions on destroy.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Initializes the two-factor authentication digit form on component creation.
   */
  ngOnInit(): void {
    this.init2FAForm();
  }

  /**
   * Gets the form array holding the two-factor authentication digit controls.
   * @return The form array of individual two-factor authentication digit controls
   */
  public get digitsFormArray(): FormArray {
    return this.digits2FAForm.get("digits") as FormArray;
  }

  /**
   * Builds the two-factor authentication form by creating one control per digit and wiring
   * each non-final digit to automatically focus the next digit input once it is filled in.
   */
  public init2FAForm(): void {
    for (let i = 0; i < this.nbDigit; i++) {
      this.digitsFormArray.push(this.fb.control("", Validators.required));
      if (i < this.nbDigit - 1) {
        (this.digitsFormArray.at(i) as FormControl).valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((value) => {
          if (value && value !== "" && !isNaN(value)) {
            // focus on next digit input
            document.getElementById("digitInput" + (i + 1))?.focus();
          }
        });
      }
    }
  }

  /**
   * Clears the value of every two-factor authentication digit control and moves focus back to
   * the first digit input, ready for a new attempt.
   */
  public reset2FAForm(): void {
    this.digitsFormArray.controls.forEach((d) => {
      d.setValue("");
    });
    document.getElementById("digitInput0")?.focus();
  }

  /**
   * Concatenates the values of the two-factor authentication digit controls into a single code.
   * @return The two-factor authentication code assembled from the digit inputs
   */
  public get2FAStringFromForm(): string {
    const digits = this.digitsFormArray.getRawValue() as number[];
    let code2FAString = "";
    digits.forEach((v) => {
      code2FAString += v;
    });
    return code2FAString;
  }

  /**
   * Strips disallowed characters from a keyboard input, reapplying the sanitized value to the
   * form control once the browser has applied its own change.
   * @param event The keyboard event that triggered the input
   * @param characters The characters to disallow
   * @param formControl The form control to sanitize
   */
  public preventCharacter(event: KeyboardEvent, characters: PREVENT_CHARACTER[], formControl: AbstractControl): void {
    // we need this timeout because the replacement must be after the change of the input value
    setTimeout(() => {
      const value = PreventCharacterHelper.preventMultiple(event, characters);
      formControl?.setValue(value);
    }, 0);
  }

  /**
   * Submits the form when Enter is pressed while focused on the password field or on the
   * last two-factor authentication digit.
   * @param field The name of the field, or the index of the digit, the key press came from
   */
  public onEnterkeyPress(field: string | number): void {
    // if user press enter when focus on password input or in the last digit of 2FA, submit the form
    if (field === "password" || field === this.nbDigit - 1) {
      this.onSubmit();
    }
  }

  /**
   * Leaves the two-factor authentication step and returns to the credentials step, resetting
   * the two-factor form and hiding the password again so it isn't redisplayed in clear text.
   */
  public goBack(): void {
    this.show2FA = false;
    this.showPassword = false;
    this.reset2FAForm();
    this.digits2FAForm.markAsUntouched();
  }

  /**
   * Distributes pasted digits across the two-factor authentication digit controls, starting from
   * the input where the paste occurred. Any non-digit character in the pasted text (e.g. spaces
   * or dashes) is ignored, and the pasted digits are used as-is even when there are fewer or more
   * of them than there are remaining inputs, rather than requiring an exact six-digit match.
   * @param event The clipboard event that triggered the paste
   * @param startIndex The index of the digit input where the paste occurred
   */
  public pasteEvent(event: ClipboardEvent, startIndex: number): void {
    event.preventDefault();

    const pasted = event.clipboardData?.getData("text") ?? "";
    const digits = pasted
      .replace(/\D/g, "")
      .split("")
      .slice(0, this.nbDigit - startIndex);
    digits.forEach((digit, offset) => {
      this.digitsFormArray.at(startIndex + offset).setValue(digit);
    });

    if (digits.length > 0) {
      const lastIndex = Math.min(startIndex + digits.length - 1, this.nbDigit - 1);
      document.getElementById("digitInput" + lastIndex)?.focus();
    }
  }

  /**
   * Handles arrow-key navigation between two-factor authentication digit inputs, and makes
   * backspace on an already-empty digit clear and refocus the previous digit instead of doing
   * nothing.
   * @param event The keyboard event that triggered the key press
   * @param index The index of the digit input the key press came from
   */
  public onKeyDown(event: KeyboardEvent, index: number): void {
    switch (event.key) {
      case "ArrowLeft":
        if (index > 0) {
          document.getElementById("digitInput" + (index - 1))?.focus();
        }
        event.preventDefault();
        return;
      case "ArrowRight":
        if (index < this.nbDigit - 1) {
          document.getElementById("digitInput" + (index + 1))?.focus();
        }
        event.preventDefault();
        return;
      case "Backspace":
        if (!this.digitsFormArray.at(index).value && index > 0) {
          this.digitsFormArray.at(index - 1).setValue("");
          document.getElementById("digitInput" + (index - 1))?.focus();
          event.preventDefault();
        }
        return;
    }
  }

  /**
   * Submits the login form. While on the credentials step, validates and submits the username
   * and password, optionally asking that the two-factor code be sent to a given telecom;
   * while on the two-factor step, validates and submits the entered code together with the
   * credentials, then resets the two-factor form. An invalid form is marked as touched instead
   * of being submitted. The form being submitted is disabled while the request is in flight and
   * re-enabled once it settles.
   * @param resendCode Whether this call re-submits the credentials step to request a new two-factor code
   * @param toTelecom Whether to send the two-factor code to telecomToSend instead of the default channel
   */
  public onSubmit(resendCode = false, toTelecom = false): void {
    if (!this.show2FA || resendCode) {
      if (this.loginForm.valid) {
        const telecom = toTelecom && this.telecomToSend ? this.telecomToSend : undefined;
        this.loginForm.disable();
        this.login(
          {
            login: this.loginForm.controls.login.value,
            password: this.loginForm.controls.password.value,
            sendCodeTo: telecom,
          },
          this.loginForm
        );
      } else {
        this.loginForm.markAllAsTouched();
      }
    } else {
      if (this.loginForm.valid && this.digits2FAForm.valid) {
        const code2FA = this.get2FAStringFromForm();
        this.digits2FAForm.disable();
        this.login(
          {
            login: this.loginForm.controls.login.value,
            password: this.loginForm.controls.password.value,
            code2FA: code2FA,
          },
          this.digits2FAForm
        );
        this.reset2FAForm();
        this.digits2FAForm.markAsUntouched();
      } else {
        this.digits2FAForm.markAllAsTouched();
      }
    }
  }

  /**
   * Attempts to log the user in and reacts to the outcome: on success, notifies the connection
   * events service and emits the submit event; on failure, sets the failure, verification and
   * two-factor state flags according to the specific error status returned by the server. These
   * flags are only ever updated here, from the latest response, rather than being cleared
   * elsewhere in the component. Once the request settles, if the two-factor step is showing, the
   * first digit input is refocused - both the first time it appears and after a failed retry -
   * so the user can immediately type the code. The focus is deferred with a timeout because change
   * detection (which renders the two-factor step's inputs, or re-enables the disabled ones) has not
   * run yet at this point, so the element isn't focusable until the next tick.
   * @param credentials Login credentials, optionally including a two-factor code or a telecom to send it to
   * @param formToReEnable The form disabled by the caller while this request is in flight, re-enabled once it settles
   */
  public login(credentials: ICredentials, formToReEnable: AbstractControl): void {
    this.authService
      .login(credentials)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          formToReEnable.enable();
          if (this.show2FA) {
            setTimeout(() => document.getElementById("digitInput0")?.focus(), 0);
          }
        })
      )
      .subscribe({
        next: (value: IOrganizationAccess) => {
          if (value.tokens.accessToken && value.tokens.refreshToken) {
            if (this.persistSession) {
              this.translateService.use(value.language);
              this.connectionEventsService.notifyLogin(value);
            }
            this.loginTokens.emit(value.tokens);
            this.loginFormSubmit.emit(true);
          } else {
            this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
              duration: 5000,
            });
            throw new Error("loginFormSubmit, no accessToken received");
          }
        },
        error: (err) => {
          if (err.status === 400) {
            this.isLoginFailed = true;
            this.isUserVerified = true;
            this.show2FA = false;
            this.is2FAFailed = false;
          } else if (err.status === 401) {
            this.isLoginFailed = false;
            this.isUserVerified = true;
            this.show2FA = true;
            this.telecoms = err.error?.telecoms?.filter((t: INoAuthUserTelecom) => t.telecomType === "email");
          } else if (err.status === 403 && err.error === "not_verified") {
            this.isLoginFailed = false;
            this.isUserVerified = false;
            this.show2FA = false;
            this.is2FAFailed = false;
          } else if (err.status === 403 && err.error === "invalid_totp") {
            this.isLoginFailed = false;
            this.isUserVerified = true;
            this.is2FAFailed = true;
          } else {
            this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
              duration: 5000,
            });
          }
        },
      });
  }

  /**
   * Emits forgotPasswordClick when the reset password flow should open in a dialog, or navigates
   * to the reset password page otherwise.
   */
  public goToResetPassword(): void {
    if (this.resetPasswordInDialog) {
      this.forgotPasswordClick.emit();
    } else {
      this.router.navigate(["reset-password"]);
    }
  }
}
